//===================================================
// 1. Load, Init, Settings
//===================================================
import axios from "axios";
import React, { useState, useEffect, useCallback } from "react";
import countries from "world-countries";

// Material UI Components
import {
  Button,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";

// Styling
import { Box } from "@mui/system";
import { useStyles } from "./index.css";
import { DEFAULT_COUNTRY_CCA3 } from "../../../common/Constants";

//===================================================
// 2. Main Component
//===================================================
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, "") ||
  "http://localhost:5000";

const CustomSearchLocation = (props) => {
  const classes = useStyles();

  // State
  const [addressInput, setAddressInput] = useState("");
  const [placePredictions, setPlacePredictions] = useState([]);
  const [showManualInput, setShowManualInput] = useState(false);
  const [manualAddress, setManualAddress] = useState({
    fullAddress: "",
    street: "",
    suburb: "",
    state: "",
    country: "Australia",
    zipcode: "",
    longitude: "",
    latitude: "",
  });

  // Fetch address predictions from Google Places Autocomplete API
  const fetchPlacePredictions = async (query, selectedCountry) => {
    if (!query) {
      setPlacePredictions([]);
      return;
    }
    try {
      // Convert country code from cca3 to cca2 format if needed
      const countryCode =
        selectedCountry?.length === 3
          ? countries.find((c) => c.cca3 === selectedCountry)?.cca2
          : selectedCountry;

      const response = await axios.get(
        `${BACKEND_URL}/api/google-maps/autocomplete`,
        {
          params: {
            input: query,
            components: countryCode
              ? `country:${countryCode.toLowerCase()}`
              : undefined,
            language: "en",
            types: "address", // Only show specific addresses
          },
        }
      );

      // Filter predictions to only include valid addresses
      const validPredictions = (response.data.predictions || []).filter(
        (prediction) => {
          // Exclude predictions that are just regions, localities, or postal codes
          const types = prediction.types || [];
          return (
            !types.includes("locality") &&
            !types.includes("administrative_area_level_1") &&
            !types.includes("administrative_area_level_2") &&
            !types.includes("postal_code")
          );
        }
      );

      setPlacePredictions(validPredictions);
    } catch (error) {
      console.error("Error fetching autocomplete results:", error);
    }
  };

  // Select address from Google Places Autocomplete
  const onSelectAddress = async (item) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/google-maps/place-details`,
        {
          params: {
            place_id: item.place_id,
            fields: "formatted_address,geometry,address_components",
          },
        }
      );

      if (!response.data.result) {
        console.error("No location details found");
        return;
      }

      const result = response.data.result;
      const { geometry, address_components, formatted_address } = result;

      // Extract address components
      const addressComponentsMap = {};
      address_components.forEach((component) => {
        component.types.forEach((type) => {
          addressComponentsMap[type] = component.long_name;
        });
      });

      // Build street address with number if available
      const streetNumber = addressComponentsMap["street_number"] || "";
      const route = addressComponentsMap["route"] || "";
      const street =
        streetNumber && route
          ? `${streetNumber} ${route}`
          : route || streetNumber;

      const streetFromComponents =
        street || addressComponentsMap["sublocality"] || "";
      // Places often omits `route` for some results; use first line of formatted address.
      const streetResolved =
        streetFromComponents ||
        (formatted_address ? formatted_address.split(",")[0].trim() : "");

      const formattedAddress = {
        fullAddress: formatted_address || "",
        street: streetResolved,
        suburb:
          addressComponentsMap["locality"] ||
          addressComponentsMap["sublocality"] ||
          addressComponentsMap["administrative_area_level_2"] ||
          "",
        state: addressComponentsMap["administrative_area_level_1"] || "",
        zipcode: addressComponentsMap["postal_code"] || "",
        country: addressComponentsMap["country"] || "Australia",
        longitude: geometry.location.lng || "",
        latitude: geometry.location.lat || "",
      };

      // Validate that we have at least the essential fields
      if (!formattedAddress.suburb || !formattedAddress.state) {
        console.error(
          "Address validation failed - missing essential fields:",
          formattedAddress
        );
        alert(
          "This address is incomplete. Please select a more specific address or enter manually."
        );
        return;
      }

      setAddressInput(formatted_address);
      setPlacePredictions([]);
      setShowManualInput(false);
      showNextStep(formattedAddress, props);
    } catch (error) {
      console.error("Error fetching geolocation:", error);
    }
  };

  return showMain({
    ...props,
    classes,
    addressInput,
    setAddressInput,
    placePredictions,
    fetchPlacePredictions,
    onSelectAddress,
    showManualInput,
    setShowManualInput,
    manualAddress,
    setManualAddress,
    setPlacePredictions,
  });
};

//===================================================
// 3. Export
//===================================================
export default CustomSearchLocation;

//===================================================
// 4. Functions
//===================================================
const showMain = (props) => {
  const { type, setPlacePredictions } = props;
  return type === "home"
    ? ShowSearchForHomePage({ ...props, setPlacePredictions })
    : ShowSearchForForm({ ...props, setPlacePredictions });
};

// **Form for entering or searching addresses**
const ShowSearchForForm = (props) => {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const {
    addressInput,
    setAddressInput,
    placePredictions,
    fetchPlacePredictions,
    onSelectAddress,
    showManualInput,
    setShowManualInput,
    manualAddress,
    setManualAddress,
    selectedCountry,
    setPlacePredictions,
    hasError,
  } = props;

  const handleSaveManualAddress = () => {
    if (
      !manualAddress.street ||
      !manualAddress.suburb ||
      !manualAddress.state ||
      !manualAddress.zipcode
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    const fullManualAddress = `${manualAddress.street}, ${manualAddress.suburb}, ${manualAddress.state}, ${manualAddress.zipcode}, Australia`;
    
    // Create immediate updated address object instead of relying on async setManualAddress state
    const updatedAddress = {
      ...manualAddress,
      fullAddress: fullManualAddress
    };
    
    setManualAddress(updatedAddress);
    setAddressInput(fullManualAddress);

    showNextStep(updatedAddress, props);
    setShowManualInput(false);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setAddressInput(value);

    if (!selectedCountry) {
      setOpenSnackbar(true);
      setIsDisabled(true);
      setAddressInput("");
    } else {
      fetchPlacePredictions(value, selectedCountry);
    }
  };

  useEffect(() => {
    if (selectedCountry) {
      setIsDisabled(false);
      setAddressInput("");
      setOpenSnackbar(false);
      setPlacePredictions([]);
    }
  }, [selectedCountry, setAddressInput, setPlacePredictions]);

  return (
    <Container align="left" sx={{ width: "100%" }}>
      <Box sx={{ zIndex: "1051 !important", mt: 2 }}>
        {!showManualInput ? (
          <>
            <TextField
              sx={{ backgroundColor: "white", borderRadius: "15px" }}
              type="text"
              onChange={handleInputChange}
              value={addressInput}
              placeholder="Search Address"
              fullWidth
              disabled={isDisabled}
              error={Boolean(hasError)}
              helperText={hasError}
            />

            <Snackbar
              open={openSnackbar}
              autoHideDuration={3000}
              onClose={() => setOpenSnackbar(false)}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <Alert
                onClose={() => setOpenSnackbar(false)}
                severity="warning"
                sx={{ width: "100%" }}
              >
                Please choose a country first.
              </Alert>
            </Snackbar>

            {placePredictions.length > 0 && (
              <Paper sx={{ p: 2, my: 2 }}>
                <Typography
                  variant="caption"
                  sx={{ color: "gray", fontWeight: "bold" }}
                >
                  Select location
                </Typography>
                {placePredictions.map((item, index) => (
                  <Typography
                    key={index}
                    sx={{
                      cursor: "pointer",
                      "&:hover": {
                        color: "white",
                        backgroundColor: "#4169e1",
                        borderRadius: "10px",
                      },
                      mt: 1,
                      p: 1,
                    }}
                    onClick={() => onSelectAddress(item)}
                  >
                    {item.description}
                  </Typography>
                ))}
              </Paper>
            )}
            <Button onClick={() => setShowManualInput(true)}>
              Enter Address Manually
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h6">Enter Address Manually</Typography>
            <TextField
              label="Street"
              fullWidth
              required
              onChange={(e) =>
                setManualAddress({ ...manualAddress, street: e.target.value })
              }
            />
            <TextField
              label="Suburb"
              fullWidth
              required
              onChange={(e) =>
                setManualAddress({ ...manualAddress, suburb: e.target.value })
              }
            />
            <TextField
              label="State"
              fullWidth
              required
              onChange={(e) =>
                setManualAddress({ ...manualAddress, state: e.target.value })
              }
            />
            <TextField
              label="Zip Code"
              fullWidth
              required
              onChange={(e) =>
                setManualAddress({ ...manualAddress, zipcode: e.target.value })
              }
            />
            <Button onClick={handleSaveManualAddress}>Save Address</Button>
            <Button onClick={() => setShowManualInput(false)}>Cancel</Button>
          </>
        )}
      </Box>
    </Container>
  );
};

// **ShowSearchForHomePage Function**
const ShowSearchForHomePage = (props) => {
  const {
    selectedCountry: parentSelectedCountry,
    setSelectedCountry: parentSetSelectedCountry,
  } = props;
  const [selectedCountry, setSelectedCountry] = useState(
    parentSelectedCountry || DEFAULT_COUNTRY_CCA3
  );

  const handleCountryChange = (country) => {
    setSelectedCountry(country);
    if (parentSetSelectedCountry) {
      parentSetSelectedCountry(country);
    }
  };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          Country
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          We use this to show accurate addresses and pricing for your region.
        </Typography>
        <SelectCountry
          selectedCountry={selectedCountry}
          setSelectedCountry={handleCountryChange}
        />
      </Box>
      <ShowSearchForForm {...props} selectedCountry={selectedCountry} />
    </Stack>
  );
};

// **SelectCountry Function**

const formattedCountries = countries.map((country) => ({
  name: country.name.common,
  cca3: country.cca3,
  countryCode: country.cca2,
  flag: `https://flagcdn.com/w40/${country.cca2.toLowerCase()}.png`,
}));

const SelectCountry = ({ selectedCountry, setSelectedCountry }) => {
  const [loading] = useState(false); // No API call, so no need for loading state

  const fetchUserLocation = useCallback(
    async (latitude, longitude) => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/google-maps/geocode`,
          {
            params: {
              latlng: `${latitude},${longitude}`,
            },
          }
        );

        if (response.data.results.length > 0) {
          const result = response.data.results[0];
          const addressComponents = result.address_components;

          const countryComponent = addressComponents.find((component) =>
            component.types.includes("country")
          );

          const userCountryName = countryComponent?.long_name;
          const matchedCountry = formattedCountries.find(
            (c) => c.name.toLowerCase() === userCountryName?.toLowerCase()
          );

          if (matchedCountry) setSelectedCountry(matchedCountry.cca3);
        }
      } catch (error) {
        console.error("Error fetching user location:", error);
      }
    },
    [setSelectedCountry]
  );

  useEffect(() => {
    if (loading || selectedCountry) return;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) =>
          fetchUserLocation(
            position.coords.latitude,
            position.coords.longitude
          ),
        (error) => console.error("Error getting geolocation:", error),
        { enableHighAccuracy: true }
      );
    }
  }, [loading, selectedCountry, fetchUserLocation]);

  return (
    <Box
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems={{ xs: "stretch", sm: "center" }}
      gap={2}
    >
      <Select
        id="country-select"
        value={selectedCountry || ""}
        onChange={(e) => setSelectedCountry(e.target.value)}
        displayEmpty
        fullWidth
        sx={{ minWidth: { sm: 260 }, maxWidth: { sm: 400 } }}
        MenuProps={{
          PaperProps: {
            sx: { maxHeight: 288, overflowY: "auto", minWidth: 260, mt: 0.5 },
          },
        }}
        renderValue={(selected) => {
          if (!selected) return "Select a Country";

          const selectedCountryObj = formattedCountries.find(
            (country) => country.cca3 === selected
          );
          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {selectedCountryObj?.flag && (
                <img
                  src={selectedCountryObj.flag}
                  alt={selectedCountryObj.name}
                  style={{ width: 20, height: 15 }}
                />
              )}
              <span>{selectedCountryObj?.name}</span>
            </Box>
          );
        }}
      >
        <MenuItem value="" disabled>
          Select a Country
        </MenuItem>
        {formattedCountries.map(({ cca3, name, flag }) => (
          <MenuItem
            key={cca3}
            value={cca3}
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {flag && (
              <img src={flag} alt={name} style={{ width: 20, height: 15 }} />
            )}
            <span>{name}</span>
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

//===================================================
// 5. Actions
//===================================================
const showNextStep = (place, props) => {
  const { handleShowDateTime, setAddress, type } = props;
  if (type !== "form") {
    handleShowDateTime && handleShowDateTime();
  }
  setAddress && setAddress(place);
};
